import styled, { css, keyframes } from "styled-components";
import { resetInput } from "styled-manila";
import { getColor, typeScale, StyleVars } from "core/utils";

const validationBorder = css`
	${props => !props.valid && css`
		border-color: ${getColor("salmon")};
	`}
`;

export const Wrapper = styled.div`
	padding-bottom: ${typeScale(1)};
	margin-bottom: 30px;
	font-size: 14px;
`;

export const FieldContainer = styled.div`
	margin-top: 20px;
	font-size: 14px;

	${props =>
		props.isModal &&
		css`
			margin: 20px;
			display: flex;

			@media (min-width: 1024px) {
				margin: 20px 10px;
			}
		`}
`;

export const Field = styled.div`
	width: 100%;
`;

export const FieldIndex = styled.div`
	font-size: 13px;
	width: 35px;
	margin-right: 20px;
`;

export const FieldDescription = styled.div`
	${({ isModal }) => !isModal && "display: flex;"}
	margin-bottom: 10px;
	${props =>
		!props.valid &&
		css`
			color: ${getColor("salmon")};
			font-weight: 600;
		`}
`;

export const RequiredIcon = styled.span`
	color: ${getColor("salmon")};
	font-weight: 600;
`;

export const SurveyFieldsContainer = styled.div`
	margin-top: 20px;
`;

export const PersonalizeButton = styled.button`
	margin: 10px 15px 10px 0;
	padding: 10px 20px;
	background: none;
	border-radius: 20px;
	border: 1px solid ${getColor("gray2")};
	text-align: center;
	white-space: nowrap;

	${props =>
		props.missingRequiredFields &&
		css`
			border: ${getColor("salmon")} 1px solid;
			background: ${getColor("lightRed")};
		`}
`;

export const ModalHeader = styled.div`
	display: flex;
	align-items: flex-end;
	margin-top: 20px;
`;

export const ModalTitle = styled.h3`
	font-size: 24px;
	margin: 0;
`;

export const ModalFooterContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 10px;
	margin: 20px;
	gap: 10px;
`;

export const SurveyStatus = styled.div`
	color: ${getColor("malachite")};
	margin-left: 20px;
`;

export const ModalToggleContainer = styled.div`
	display: flex;
	align-items: center;
`;

export const ErrorMessage = styled.div`
	color: ${getColor("salmon")};
	font-size: 12px;
`;

export const Label = styled.label`
	${resetInput}
	margin-left: 6px;
	font-size: 13px;
	line-height: 110%;
	transition: 0.3s ease-in-out;
	font-weight: 400;
	display: inline;
`;

export const TextArea = styled.textarea`
	${resetInput}
	width: 100%;
	height: 64px;
	outline: none;
	margin: 0;
	padding: 10px 1em;
	resize: vertical;
	border: 1px solid
		${props => (props.border ? getColor("alto") : "transparent")};
	border-radius: ${StyleVars.defaultBorderRadius};
	transition: 0.2s ease-in;

	${validationBorder}

	&:focus,
	&:active {
		border: 1px solid ${getColor("primaryBrand")};
	}
`;

export const SurveyInput = styled.input`
	${resetInput}
	width: 100%;
	height: 40px;
	outline: none;
	margin: 0;
	padding: 10px 1em;
	resize: none;
	border: 1px solid
		${props => (props.border ? getColor("alto") : "transparent")};
	border-radius: ${StyleVars.defaultBorderRadius};

	${validationBorder}

	&:focus,
	&:active {
		border: 1px solid ${getColor("primaryBrand")};
	}
`;

export const CheckboxContainer = styled.div`
	${props =>
		props.isModal &&
		css`
			display: grid;
			grid-gap: 5px;
			grid-template-columns: 1fr 1fr 1fr;

			@media (max-width: 1024px) {
				grid-template-columns: 1fr;
			}
		`}
`;

export const CheckboxLabel = styled.label`
	${resetInput}
	display: block;

	input {
		margin-right: 5px;
	}

	${props =>
		props.isModal &&
		css`
			@media (min-width: 1024px) {
				&:focus,
				&:active {
					border: 1px solid ${getColor("primaryBrand")};
				}
				padding: 16px;
				border: 1px solid ${getColor("gray3")};
			}
		`}
`;

export const DatePickerContainer = styled.div`
	position: relative;
`;

// Placeholder

const PLACEHOLDER_MIN_HEIGHT = 155;

const shimmer = keyframes`
	0% { background-position: -468px 0; }
	100% { background-position: 468px 0; }
`;

const backgroundShimmer = height => css`
	animation: ${shimmer} 1s linear infinite forwards;
	background: ${getColor("porcelain")};
	background: linear-gradient(
		to right,
		${getColor("gray1")} 8%,
		${getColor("gray2")} 18%,
		${getColor("gray1")} 33%
	);
	background-size: 800px 104px;
	height: ${height || PLACEHOLDER_MIN_HEIGHT}px;
	position: relative;
`;

// Outer container with that background shimmer
export const Placeholder = styled.div`
	background: ${getColor("white")};
	min-height: ${props => props.withHeight || PLACEHOLDER_MIN_HEIGHT}px;
	${backgroundShimmer(PLACEHOLDER_MIN_HEIGHT)};
`;
